import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import * as THREE from "three";
import TopBar from "../__shared__/TopBar";
import {
    setCurrentTreatmentPlan,
    setStepActive,
    setStepComplete,
} from "../CreateTPlan_1Upload/actions";
import { withRouter } from "react-router";
import PropTypes from "prop-types";
import RequirementsModal from "../__shared__/RequirementsModal";
import ProfileMenu from "../__shared__/ProfileMenu";
import {
    EMPTY_STRING,
    pageLoadStates,
    sceneOrientations,
    stepPath,
    USER_MANUAL_URL,
} from "../../constants";
import StepMarkerBar from "../__shared__/StepMarkerBar";
import PreviousStepButton from "../__shared__/PreviousStepButton";
import NextStepButton from "../__shared__/NextStepButton";
import { getPlan } from "../CreateTPlan_1Upload/helpers";
import { getCemWarningMsg } from "./helpers";
import {
    loadBxCores,
    loadMri,
    loadProstate,
    loadROIs,
    loadMargin,
    loadOtherStructures,
    resetViewer,
} from "../../helpers/load_helpers";
import * as TreatmentPlan from "../../helpers/tplan/tplan";
import ReviewCancerLesion from "./ReviewCancerLesion";
import MRIModel from "../__shared__/Viewers/Structures/MRI/MRIModel";
import PatientAnatomyController from "../__shared__/Viewers/Structures/PatientAnatomy/PatientAnatomyController";
import PatientAnatomyModel from "../__shared__/Viewers/Structures/PatientAnatomy/PatientAnatomyModel";
import DecisionAlertCard from "../__shared__/DecisionAlertCard";
import {
    BIOPSY_STEP,
    EDIT_MARGIN_STEP,
    SUMMARY_STEP,
} from "../CreateTPlan_1Upload/constants";
import { updateTreatmentPlan } from "../../helpers/backend_api";
import MRIViewer from "../__shared__/Viewers/MRIViewer";
import MRIController from "../__shared__/Viewers/Structures/MRI/MRIController";
import MeshViewer from "../__shared__/Viewers/MeshViewer";
import MRIViewerButton from "../__shared__/MRIViewerButton";
import {
    mriViewerButtonTooltip,
    RESET_VIEW,
} from "../CreateTPlan_2SegMRI/constants";
import cubeBlue from "../../assets/nav_cube_blue.svg";
import cubeLight from "../../assets/nav_cube_light.svg";
import cubeDark from "../../assets/nav_cube_dark.svg";
import filterBlue from "../../assets/filter_blue.svg";
import filterLight from "../../assets/filter_light.svg";
import filterDark from "../../assets/filter_dark.svg";
import NavCubeModal from "../__shared__/NavCubeModal";
import FilterToggle from "../__shared__/FilterToggle";
import SplitPane from "react-split-pane";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
    ChooseLesionSizeTooltip,
    STEP4_BUTTON_TEXT,
    STEP4_CEM_WARNING,
    STEP4_TEXT,
} from "./constants";
import ButtonGroup from "../__shared__/ButtonGroup";
import SliceControl from "../__shared__/SliceControl";
import ResetViewerButton from "../__shared__/ResetViewerButton";
import WarningTriangle from "../__shared__/IconComponents/WarningTriangle";
import {
    addPendoTracking,
    getLoadingScreen,
    isEmpty,
} from "../../helpers/helpers";
import { trackingEventNames } from "../../helpers/pendo_constants";
import { setDebugHooks } from "../../helpers/debug/debug_hooks";
import DownloadSelectCard from "../__shared__/DownloadSelectCard";
import { setDownloadProperties } from "../Home/actions";
import QuestionIcon from "../../ui/icons/QuestionIcon";
import CaseExportIcon from "../../ui/icons/CaseExportIcon";
import UnfoldLogo from "../../ui/logos/UnfoldLogo";
import ScalpelIcon from "../../ui/icons/ScalpelIcon";
import CapsuleRiskMapIcon from "../../ui/icons/CapsuleRiskMapIcon";
import CapsuleRiskMap from "./CapsuleRiskMap/CapsuleRiskMap";
import { setSurgeryMode } from "../../redux/surgery_mode/actions";
import { setViewerOrientation } from "../CreateTPlan_2SegMRI/actions";
import { getMRIViewOrientation } from "./helpers";
import SurgeryModeConfirmModal, {
    isSurgeryModeIntroDismissed,
} from "../__shared__/SurgeryModeConfirmModal";
import RepeatIcon from "../../ui/icons/RepeatIcon";
import ExitFullscreenIcon from "../../ui/icons/ExitFullscreenIcon";

function CreateTPlan_4CancerLesion(props) {
    const {
        authToken,
        useruuid,
        patientuuid,
        planuuid,
        visituuid,
        downloadProperties,
        setCurrentTreatmentPlan,
        setDownloadProperties,
    } = props;

    // States
    const [loadState, setLoadState] = useState(pageLoadStates.LOADING);
    const [cemWarningMsg, setCemWarningMsg] = useState(null);
    const [showCemWarningModal, setShowCemWarningModal] = useState(false);
    const [step4Requirements, setStep4Requirements] = useState({
        required: [
            {
                id: "margin-req",
                satisfied: false,
                reqText:
                    "Confirm a cancer lesion contour if Gleason Grade Group > 1",
                listIndex: 1,
            },
        ],
    });
    const [autoLevel, setAutoLevel] = useState(false);
    const [inSurgeryMode, setInSurgeryMode] = useState(false);
    const [showSurgeryConfirm, setShowSurgeryConfirm] = useState(false);
    const [showSurgeryCRM, setShowSurgeryCRM] = useState(false);
    const [splitSize, setSplitSize] = useState("60%"); // default size of 2D viewer
    const crmEnabled = import.meta.env.VITE_SHOW_CRM === "true";

    const dispatch = useDispatch();
    const surgeryModeActive = useSelector(
        (state) => state.SurgeryModeReducer.autoLogoutPaused
    );
    const viewerOrientation = useSelector(
        (state) => state.CreatePlanSegReducer.viewerOrientation
    );

    // Refs
    const mriRef = useRef(null);
    const meshRef = useRef(null);
    const sliceControlRef = useRef(null);
    const requirementsModalRef = useRef(null);
    const prevOrientationRef = useRef(null);
    const surgeryEnterTimeRef = useRef(null);

    // Models and Controllers
    const mriModel = useRef(new MRIModel(true));
    const mriController = useRef(new MRIController(mriModel.current));
    const patientAnatomyModel = useRef(new PatientAnatomyModel());
    const patientAnatomyController = useRef(
        new PatientAnatomyController(patientAnatomyModel.current)
    );

    function handleCemWarnings(plan) {
        const cemWarnings = plan.SavedData.CEMWarnings;
        if (cemWarnings) {
            const warningString = getCemWarningMsg(cemWarnings);
            if (warningString !== EMPTY_STRING) {
                setCemWarningMsg(warningString);
                setShowCemWarningModal(true);
            }
        }
    }

    function handleDownloadButtonClick() {
        setDownloadProperties(true, patientuuid, planuuid);
    }

    function getMriUrls(plan) {
        const ids = {
            token: authToken,
            useruuid: useruuid,
            patientuuid: patientuuid,
            planuuid: planuuid,
            visituuid: visituuid,
        };

        return loadMri({
            ids,
            currentTreatmentPlan: plan,
            mriModel: mriModel.current,
            patientAnatomyController: patientAnatomyController.current,
            loadState: loadState,
            setLoadState: setLoadState,
        }).then(() => {
            setAutoLevel(mriModel.current.initializeAutoLevel());
        });
    }

    function getMeshUrls(plan) {
        const ids = {
            token: authToken,
            useruuid: useruuid,
            patientuuid: patientuuid,
            planuuid: planuuid,
            visituuid: visituuid,
        };

        loadBxCores({
            plan,
            patientAnatomyModel: patientAnatomyModel.current,
        });
        loadROIs({
            plan,
            ids,
            patientAnatomyModel: patientAnatomyModel.current,
            patientAnatomyController: patientAnatomyController.current,
        });
        loadMargin({
            plan,
            ids,
            patientAnatomyModel: patientAnatomyModel.current,
            setLoadState,
        });
        loadOtherStructures({
            plan,
            ids,
            patientAnatomyModel: patientAnatomyModel.current,
        });
        return loadProstate({
            plan,
            ids,
            patientAnatomyModel: patientAnatomyModel.current,
            createBoundingCone: true,
            createAxes: true,
            setLoadState,
        });
    }

    function zoomMRIViewerToFit() {
        // World-axis-aligned bounding box of prostate mesh in MRI viewer scene
        const box = new THREE.Box3();
        box.setFromObject(
            patientAnatomyModel.current.prostate.selfMRIView.scene,
            true
        );

        // Bounding sphere enclosing bounding box so we don't have to worry about handling rotated axes
        // May not be as precise as possible, but should completely enclose mesh
        // Could also get a more precise bounding sphere from mesh geometry, but would then have to manually
        // apply transforms (which may include scale)
        const sphere = new THREE.Sphere();
        box.getBoundingSphere(sphere);

        // Transform bounding sphere center so it is in camera's space
        const center = sphere.center;
        const camera = mriRef.current.camera;
        camera.updateMatrixWorld(true);
        center.applyMatrix4(camera.matrixWorldInverse);

        // Set zoom so that bounding sphere is in camera's view frustum
        const radius = sphere.radius;
        let zoom = (camera.right - center.x) / radius;
        zoom = Math.min((center.x - camera.left) / radius, zoom);
        zoom = Math.min((camera.top - center.y) / radius, zoom);
        zoom = Math.min((center.y - camera.bottom) / radius, zoom);
        camera.zoom = Math.min(
            Math.max(zoom, camera.controls.minZoom),
            camera.controls.maxZoom
        );
        camera.updateProjectionMatrix();
    }

    useEffect(() => {
        // Right now when the auth token is re-authorized and updated, this
        // effect triggers. If the plan is already loaded, then without this
        // check the plan would be re-loaded again and an entirely new scene
        // created, resulting in multiple MRI and mesh viewers.
        if (loadState === pageLoadStates.COMPLETE) {
            return;
        }

        getPlan(authToken, patientuuid, planuuid)
            .then((payload) => payload.json())
            .then((json) => {
                let plan = json.payload.plan;

                if (!plan.SavedData.CPMURI) {
                    setLoadState(pageLoadStates.CEM_FAILED);
                    return;
                }

                setStepComplete(BIOPSY_STEP, true);
                setStepActive(EDIT_MARGIN_STEP, true);
                setCurrentTreatmentPlan(plan);
                handleCemWarnings(plan);
                return Promise.all([getMriUrls(plan), getMeshUrls(plan)]);
            })
            .then(() => {
                zoomMRIViewerToFit();
            })
            .catch(() => {
                setLoadState(pageLoadStates.FAILED);
            });
        setDebugHooks({
            mriModel: mriModel.current,
            patientAnatomyModel: patientAnatomyModel.current,
        });
    }, [authToken, patientuuid, planuuid, setCurrentTreatmentPlan]);

    function openSurgeryMode() {
        // Capture the current orientation so we can restore it on exit,
        // then go fullscreen on the viewer panel
        prevOrientationRef.current = mriModel.current.orientation;
        const root = document.documentElement;
        if (root.requestFullscreen) {
            root.requestFullscreen().catch(() => {});
        }
    }

    function handleScalpelClick() {
        if (inSurgeryMode) {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(() => {});
            }
            return;
        }
        if (isSurgeryModeIntroDismissed()) {
            openSurgeryMode();
        } else {
            setShowSurgeryConfirm(true);
        }
    }

    function confirmSurgeryMode() {
        setShowSurgeryConfirm(false);
        openSurgeryMode();
    }

    function trackSurgeryModeDuration() {
        if (!surgeryEnterTimeRef.current) {
            return;
        }

        const enterMs = surgeryEnterTimeRef.current;
        const exitMs = Date.now();
        const totalSeconds = Math.round((exitMs - enterMs) / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        addPendoTracking(
            trackingEventNames.SURGERY_MODE_SESSION,
            planuuid,
            null,
            {
                enteredAt: new Date(enterMs).toISOString(),
                exitedAt: new Date(exitMs).toISOString(),
                durationTime: `${String(minutes).padStart(2, "0")} minutes ${String(seconds).padStart(2, "0")} seconds`,
            }
        );
        surgeryEnterTimeRef.current = null;
    }

    // Reconcile React/Redux/viewer state with the actual fullscreen status.
    // Listening to fullscreenchange (rather than only our buttons) means the Esc
    // key and the 8-hour cap follow the same enter/exit path.
    useEffect(() => {
        function handleFullscreenChange() {
            const isFullscreen =
                document.fullscreenElement === document.documentElement;
            setInSurgeryMode(isFullscreen);

            if (isFullscreen) {
                surgeryEnterTimeRef.current = Date.now();
                dispatch(setSurgeryMode(true));
                mriModel.current.orientation =
                    sceneOrientations.SURGERY_CORONAL;
                dispatch(
                    setViewerOrientation(sceneOrientations.SURGERY_CORONAL)
                );
                setSplitSize("35%");
            } else {
                dispatch(setSurgeryMode(false));
                setSplitSize("60%");
                trackSurgeryModeDuration();

                const prevOrientation = prevOrientationRef.current;
                if (prevOrientation) {
                    mriModel.current.orientation = prevOrientation;
                    dispatch(setViewerOrientation(prevOrientation));
                }
            }

            if (sliceControlRef.current) {
                sliceControlRef.current.reset();
            }

            // Re-fit the viewers after the fullscreen transition
            requestAnimationFrame(() => {
                if (mriRef.current) {
                    mriRef.current.onWindowResize();
                }
                if (meshRef.current) {
                    meshRef.current.onWindowResize();
                }
                zoomMRIViewerToFit();
            });
        }

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () =>
            document.removeEventListener(
                "fullscreenchange",
                handleFullscreenChange
            );
    }, [dispatch]);

    // If the surgery-mode flag is cleared elsewhere (e.g. SessionManager's
    // 8-hour cap) while we're still fullscreen, exit fullscreen so the two
    // stay in sync.
    useEffect(() => {
        if (
            !surgeryModeActive &&
            document.fullscreenElement === document.documentElement
        ) {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(() => {});
            }
        }
    }, [surgeryModeActive]);

    function handleMarginCreated() {
        const tPlan = props.currentTreatmentPlan.TreatmentPlan;
        if (isEmpty(tPlan) || !TreatmentPlan.hasMargins(tPlan)) {
            return;
        }

        const ids = {
            token: props.authToken,
            useruuid: props.useruuid,
            patientuuid: props.patientuuid,
            planuuid: props.planuuid,
            visituuid: props.visituuid,
        };

        patientAnatomyModel.current.margin = null;

        loadMargin({
            plan: props.currentTreatmentPlan,
            ids,
            patientAnatomyModel: patientAnatomyModel.current,
            setLoadState,
        });
    }

    function renderCemWarningModal() {
        return (
            <div className="fixed z-50 h-full w-full bg-black/50">
                <DecisionAlertCard
                    id="cem-warning-modal"
                    singleBtn={true}
                    yesBtnTxt={STEP4_BUTTON_TEXT.CONFIRM}
                    yesBtnCallback={() => setShowCemWarningModal(false)}
                >
                    <div className="p-6 text-white">
                        <div className="mb-4 flex items-center gap-2">
                            <WarningTriangle width="32" height="27" />
                            <div className="text-2xl font-medium">
                                {STEP4_CEM_WARNING.TITLE}
                            </div>
                        </div>
                        <p className="mb-4">{cemWarningMsg}</p>
                        <p>
                            {STEP4_CEM_WARNING.FOOTER_PREFIX}{" "}
                            <a
                                className="text-blue-600 hover:text-blue-500"
                                href={USER_MANUAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {STEP4_CEM_WARNING.USER_MANUAL}
                            </a>
                            .
                        </p>
                    </div>
                </DecisionAlertCard>
            </div>
        );
    }

    function verifyStep4Requirements() {
        const gleasonGradeGroup = TreatmentPlan.getGleasonGradeGroup(
            props.currentTreatmentPlan.TreatmentPlan
        );
        const isMarginReqSatisfied =
            gleasonGradeGroup < 2 ||
            TreatmentPlan.hasMargins(props.currentTreatmentPlan.TreatmentPlan);

        setStep4Requirements({
            required: [
                {
                    id: "margin-req",
                    satisfied: isMarginReqSatisfied,
                    reqText:
                        "Confirm a cancer lesion contour if Gleason Grade Group > 1",
                    listIndex: 1,
                },
            ],
        });

        return isMarginReqSatisfied;
    }

    function renderStepHeader() {
        const renderTooltip = (props) => (
            <Tooltip id="rev-cancer-lesion-qm" {...props}>
                {ChooseLesionSizeTooltip}
            </Tooltip>
        );

        return (
            <div className="flex min-h-14 w-full items-center justify-between bg-[#262626] px-4">
                <div className="flex flex-col text-white">
                    <div className="text-xl font-medium">
                        {STEP4_TEXT.HEADING}
                    </div>
                    <div className="text-sm">{STEP4_TEXT.SUB_HEADING}</div>
                </div>
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={renderTooltip}
                >
                    <div className="cursor-pointer">
                        <QuestionIcon />
                    </div>
                </OverlayTrigger>
            </div>
        );
    }

    function renderViewerControlBar() {
        return (
            <div className="right-0 flex h-14 w-full items-center bg-[#262626]">
                <ButtonGroup>
                    <MRIViewerButton
                        toolTipText={mriViewerButtonTooltip.SCENE_ORIENTATION}
                        imgBlue={cubeBlue}
                        imgDark={cubeDark}
                        imgLight={cubeLight}
                        name="nav-cube"
                        alt="change MRI viewer orientation button"
                    >
                        <NavCubeModal
                            mriModel={mriModel.current}
                            resetTick={() => sliceControlRef.current.reset()}
                        />
                    </MRIViewerButton>
                    <MRIViewerButton
                        toolTipText={mriViewerButtonTooltip.FILTER}
                        imgBlue={filterBlue}
                        imgDark={filterDark}
                        imgLight={filterLight}
                        name="filter"
                        alt={mriViewerButtonTooltip.FILTER}
                    >
                        <FilterToggle
                            mriModel={mriModel.current}
                            patientAnatomyModel={patientAnatomyModel.current}
                            displaySettings={{
                                axesEnabled: true,
                                mriEnabled: true,
                                noMRIOption: true,
                            }}
                            autoLevel={autoLevel}
                            setAutoLevel={setAutoLevel}
                            hideColorKey={inSurgeryMode}
                        />
                    </MRIViewerButton>
                </ButtonGroup>
                <div
                    id="surgery-mode-btn"
                    className="click-outside-ignore ml-5 cursor-pointer"
                    onClick={handleScalpelClick}
                >
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={
                            <Tooltip id="surgery-mode-tooltip">
                                {inSurgeryMode
                                    ? "Exit Surgery Mode"
                                    : "Surgery Mode"}
                            </Tooltip>
                        }
                    >
                        <div
                            className={`flex h-11.5 w-11.5 items-center justify-center rounded-full ${
                                inSurgeryMode ? "bg-[#0049c6]" : ""
                            }`}
                        >
                            <ScalpelIcon size={26} />
                        </div>
                    </OverlayTrigger>
                </div>
                {inSurgeryMode && crmEnabled && (
                    <div
                        className="ml-5 cursor-pointer"
                        onClick={() => setShowSurgeryCRM((prev) => !prev)}
                    >
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id="surgery-crm-tooltip">
                                    {showSurgeryCRM
                                        ? "Hide Capsule Risk Scores"
                                        : "Show Capsule Risk Scores"}
                                </Tooltip>
                            }
                        >
                            <div
                                className={`flex h-11.5 w-11.5 items-center justify-center rounded-full ${
                                    showSurgeryCRM ? "bg-[#0049c6]" : ""
                                }`}
                            >
                                <CapsuleRiskMapIcon />
                            </div>
                        </OverlayTrigger>
                    </div>
                )}
                <SliceControl
                    ref={sliceControlRef}
                    mriModel={mriModel.current}
                    mriController={mriController.current}
                />
                <div className="absolute right-4 flex items-center gap-4">
                    <ResetViewerButton
                        toolTipText={RESET_VIEW}
                        inSurgeryMode={inSurgeryMode}
                        resetViewer={() => {
                            resetViewer(
                                meshRef,
                                mriRef,
                                sliceControlRef,
                                mriModel.current
                            );
                            zoomMRIViewerToFit();
                        }}
                    />
                    {inSurgeryMode && (
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id="surgery-exit-fullscreen-tooltip">
                                    {inSurgeryMode
                                        ? "Exit Surgery Mode"
                                        : undefined}
                                </Tooltip>
                            }
                        >
                            <ExitFullscreenIcon
                                className="cursor-pointer"
                                onClick={handleScalpelClick}
                                size={28}
                            />
                        </OverlayTrigger>
                    )}
                </div>
            </div>
        );
    }

    function handleProceedToPrevStep() {
        let plan = props.currentTreatmentPlan;

        props.history.push(stepPath.STEP3);
        props.setStepComplete(EDIT_MARGIN_STEP, false);
        props.setStepActive(EDIT_MARGIN_STEP, false);
        props.setStepActive(BIOPSY_STEP, true);
        plan.SavedData.PlanCreationStep = BIOPSY_STEP;
        setShowCemWarningModal(false);

        updateTreatmentPlan({
            useruuid: props.useruuid,
            patientuuid: props.patientuuid,
            planuuid: props.planuuid,
            token: props.authToken,
            plan: plan,
        });
        props.setCurrentTreatmentPlan(plan);
    }

    function handleProceedToStep5() {
        if (!verifyStep4Requirements()) {
            requirementsModalRef.current.openModal();
            return;
        }

        // Only proceed if requirements are met
        props.history.push(stepPath.STEP5);

        props.setStepComplete(EDIT_MARGIN_STEP, true);
        props.setStepActive(EDIT_MARGIN_STEP, false);
        props.setStepActive(SUMMARY_STEP, true);

        let plan = props.currentTreatmentPlan;
        plan.SavedData.PlanCreationStep = SUMMARY_STEP;
        props.setCurrentTreatmentPlan(plan);
        updateTreatmentPlan({
            useruuid: props.useruuid,
            patientuuid: props.patientuuid,
            planuuid: props.planuuid,
            token: props.authToken,
            plan: plan,
        });
    }

    function showDownloadCard() {
        return downloadProperties.selectActive ? (
            <DownloadSelectCard
                currentTreatmentPlan={props.currentTreatmentPlan}
                pdfOnly={true}
            />
        ) : (
            EMPTY_STRING
        );
    }

    function renderSurgeryMRIViewIcon() {
        const { ViewIcon, showRepeatIcon } =
            getMRIViewOrientation(viewerOrientation);

        return (
            <div className="absolute top-6 left-8 flex items-center gap-1 opacity-40">
                {showRepeatIcon && <RepeatIcon size={26} />}
                <ViewIcon />
            </div>
        );
    }

    function renderSurgeryModeIndicator() {
        const { ViewIcon, label, showRepeatIcon } =
            getMRIViewOrientation(viewerOrientation);

        return (
            <div className="absolute right-8 bottom-6 text-white opacity-40">
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                        {showRepeatIcon && <RepeatIcon size={26} />}
                        <ViewIcon />
                    </div>
                    <span className="text-2xl">{label}</span>
                </div>
                <div className="text-4xl">Surgery Mode</div>
            </div>
        );
    }

    return (
        <div className="create-plan-edit-margin-screen h-full w-full">
            {!inSurgeryMode && (
                <TopBar>
                    <Link to={stepPath.HOME}>
                        <UnfoldLogo />
                    </Link>
                    <div className="create-plan-step-bar-container">
                        <div className="step-bar-prev-btn-group">
                            <PreviousStepButton
                                to={stepPath.STEP3}
                                callback={handleProceedToPrevStep}
                            />
                            <StepMarkerBar />
                        </div>
                        <NextStepButton
                            reqsComplete={verifyStep4Requirements}
                            reqsCompleteTxt={STEP4_BUTTON_TEXT.NEXT_STEP}
                            noLink={true}
                            callback={handleProceedToStep5}
                        />
                    </div>
                    <div
                        id="download-pdf-button"
                        className="w-13 cursor-pointer pt-2"
                        onClick={handleDownloadButtonClick}
                    >
                        <CaseExportIcon />
                    </div>
                    <ProfileMenu />
                </TopBar>
            )}
            {showDownloadCard()}
            <RequirementsModal
                id="edit-margin-pull-tab"
                onRef={(ref) => (requirementsModalRef.current = ref)}
                requirements={step4Requirements}
            />
            {getLoadingScreen(loadState, undefined, handleProceedToPrevStep)}
            {cemWarningMsg && showCemWarningModal && renderCemWarningModal()}
            <SurgeryModeConfirmModal
                show={showSurgeryConfirm}
                onConfirm={confirmSurgeryMode}
                onCancel={() => setShowSurgeryConfirm(false)}
            />
            <div
                className={
                    inSurgeryMode
                        ? "absolute top-0 flex h-full w-full"
                        : "absolute top-20 flex h-[calc(100%-74px)] w-full"
                }
            >
                <div
                    className={
                        inSurgeryMode
                            ? "hidden"
                            : "flex h-full max-w-[30vw] min-w-100 basis-[30vw] flex-col"
                    }
                >
                    {renderStepHeader()}
                    <div className="h-full w-full grow">
                        <ReviewCancerLesion
                            onMarginCreation={handleMarginCreated}
                            patientAnatomyModel={patientAnatomyModel.current}
                        />
                    </div>
                </div>
                <div
                    className={
                        inSurgeryMode
                            ? "flex h-screen w-screen flex-col bg-black"
                            : "flex max-w-[70vw] basis-[70vw] flex-col"
                    }
                >
                    {renderViewerControlBar()}
                    <div className="relative h-full w-full">
                        <SplitPane
                            className="h-full w-full"
                            size={splitSize}
                            onChange={(newSize) => setSplitSize(`${newSize}px`)}
                            minSize={10}
                            maxSize={-10}
                            split="vertical"
                            pane1ClassName="mri-pane"
                            pane2ClassName="mesh-pane"
                            onDragFinished={() => {
                                mriRef.current.onWindowResize();
                                meshRef.current.onWindowResize();
                            }}
                        >
                            <MRIViewer
                                ref={mriRef}
                                mriModel={mriModel.current}
                                mriController={mriController.current}
                                patientAnatomyModel={
                                    patientAnatomyModel.current
                                }
                            />
                            <MeshViewer
                                ref={meshRef}
                                staticViewer={true}
                                displaySettings={{
                                    mriEnabled: false,
                                    composerEnabled: true,
                                }}
                                mriModel={mriModel.current}
                                mriController={mriController.current}
                                patientAnatomyModel={
                                    patientAnatomyModel.current
                                }
                                patientAnatomyController={
                                    patientAnatomyController.current
                                }
                                activateCoreClick={false}
                            />
                        </SplitPane>
                        {inSurgeryMode && renderSurgeryMRIViewIcon()}
                        {inSurgeryMode && renderSurgeryModeIndicator()}
                        {inSurgeryMode && crmEnabled && showSurgeryCRM && (
                            <div className="absolute bottom-8 left-8 z-20">
                                <div className="mb-2 text-lg text-white opacity-40">
                                    For Axial view reference only
                                </div>
                                <div className="h-60 w-105 rounded-xl bg-[#393939] pt-3 pb-2 shadow-lg">
                                    <CapsuleRiskMap
                                        surgeryMode={true}
                                        currentTreatmentPlan={
                                            props.currentTreatmentPlan
                                        }
                                        patientAnatomyModel={
                                            patientAnatomyModel.current
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

CreateTPlan_4CancerLesion.propTypes = {
    authToken: PropTypes.string,
    useruuid: PropTypes.string,
    patientuuid: PropTypes.string,
    planuuid: PropTypes.string,
    visituuid: PropTypes.string,
    currentTreatmentPlan: PropTypes.object,
    setCurrentTreatmentPlan: PropTypes.func,
    history: PropTypes.object,
    setStepActive: PropTypes.func,
    setStepComplete: PropTypes.func,
    currentStepFromProps: PropTypes.string,
    downloadProperties: PropTypes.object,
    setDownloadProperties: PropTypes.func,
};

const mapStateToProps = function (state) {
    return {
        authToken: state.LoginReducer.authToken,
        useruuid: state.LoginReducer.useruuid,
        patientuuid: state.CreatePlanReducer.patientuuid,
        planuuid: state.CreatePlanReducer.planuuid,
        visituuid: state.CreatePlanReducer.visituuid,
        downloadProperties: state.HomeReducer.downloadProperties,
        currentTreatmentPlan: state.CreatePlanReducer.currentTreatmentPlan,
    };
};
const mapDispatchToProps = function (dispatch) {
    return {
        setCurrentTreatmentPlan: (plan) =>
            dispatch(setCurrentTreatmentPlan(plan)),
        setStepActive: (stepIdx, isActive) =>
            dispatch(setStepActive(stepIdx, isActive)),
        setStepComplete: (stepIdx, isComplete) =>
            dispatch(setStepComplete(stepIdx, isComplete)),
        setDownloadProperties: (selectOpen, patID, planID) =>
            dispatch(setDownloadProperties(selectOpen, patID, planID)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(CreateTPlan_4CancerLesion));
